import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Quote,
  CheckCircle2,
  Moon,
  Brain,
  Sunrise,
} from "lucide-react";
import { Reveal, SectionEyebrow, SoftHalo } from "@/components/Primitives";
import { CalendlyButton } from "@/components/Calendly";

const HERO_BG =
  "https://customer-assets.emergentagent.com/job_restore-sleep/artifacts/vkdskc9l_ChatGPT%20Image%20May%203%2C%202026%2C%2003_44_01%20AM.png";
const PORTRAIT =
  "https://customer-assets.emergentagent.com/job_restore-sleep/artifacts/4kykqccg_justin.jpeg";

/* ---------------- Hero ---------------- */
const Hero = () => {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[94vh] flex items-center pt-36 pb-24 hero-halo overflow-hidden"
    >
      <SoftHalo />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16 w-full">
        <div className="grid grid-cols-12 gap-10 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="col-span-12 lg:col-span-7"
          >
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-brand-50 text-brand-700 text-xs font-medium tracking-wide">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-600" />
              Now accepting clients · Q1 2026
            </div>

            <h1
              data-testid="hero-headline"
              className="mt-10 font-display font-light text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-[-0.02em] leading-[1.05] text-ink-900 break-words"
            >
              Fix your sleep.
              <br />
              <span className="text-brand-600">Rebuild your energy.</span>
            </h1>

            <p
              data-testid="hero-subheadline"
              className="mt-8 max-w-xl text-lg text-ink-600 leading-[1.7]"
            >
              Premium sleep coaching for high-performing professionals in tech
              and finance. Evidence-based protocols to overcome insomnia and
              unlock consistent energy.
            </p>

            <div className="mt-12 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 w-full">
              <CalendlyButton testId="hero-cta-primary" className="w-full sm:w-auto justify-center">
                Book a Free Sleep Assessment
              </CalendlyButton>
              <Link
                to="/services"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-7 py-3.5 rounded-full bg-white border border-ink-200 text-ink-900 hover:border-brand-600 hover:text-brand-600 transition-all text-[15px] font-medium"
              >
                Explore the Method
              </Link>
            </div>

            <div className="mt-14 flex flex-wrap gap-x-8 gap-y-3 text-sm text-ink-600">
              {[
                "Sleep Restriction Therapy",
                "Nervous System Regulation",
                "Personalized Protocols",
              ].map((t) => (
                <span key={t} className="inline-flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-brand-600" /> {t}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="col-span-12 lg:col-span-5"
          >
            <div className="relative rounded-[28px] overflow-hidden border border-ink-100 shadow-[0_12px_40px_rgba(15,23,42,0.08)] aspect-[4/5] bg-ink-50">
              <img
                src={HERO_BG}
                alt="Calm bedroom morning light"
                loading="eager"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div
                aria-hidden
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(255,255,255,0) 60%, rgba(255,255,255,0.4) 100%)",
                }}
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* ---------------- Problem ---------------- */
const Problem = () => {
  const items = [
    { tag: "01", title: "Can't fall asleep", desc: "Mind racing past midnight despite total exhaustion." },
    { tag: "02", title: "Wake up drained", desc: "Eight hours in bed. Still groggy by 10 a.m." },
    { tag: "03", title: "Foggy & unfocused", desc: "Decision fatigue creeps in earlier each week." },
    { tag: "04", title: "On the edge of burnout", desc: "Caffeine is the strategy. Recovery is missing." },
  ];

  return (
    <section
      data-testid="problem-section"
      className="relative py-24 md:py-32 bg-ink-50"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-12 gap-x-8 gap-y-12">
          <div className="col-span-12 lg:col-span-5">
            <Reveal>
              <SectionEyebrow testId="problem-eyebrow">The Pattern</SectionEyebrow>
              <h2 className="mt-6 font-display font-light text-3xl sm:text-4xl md:text-5xl tracking-tight text-ink-900 leading-[1.1]">
                Winning at work.
                <br />
                <span className="text-ink-500">Losing at sleep.</span>
              </h2>
              <p className="mt-6 text-ink-600 max-w-md leading-relaxed">
                You&apos;re not alone — the pattern is consistent across every
                client who walks through the door.
              </p>
            </Reveal>
          </div>

          <div className="col-span-12 lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {items.map((p, i) => (
                <Reveal key={p.tag} delay={i * 0.05}>
                  <div
                    data-testid={`problem-card-${i}`}
                    className="surface-soft p-6 h-full"
                  >
                    <div className="flex items-baseline justify-between">
                      <span className="font-display text-sm tracking-[0.25em] text-brand-600 num-tag">
                        {p.tag}
                      </span>
                    </div>
                    <h3 className="mt-4 font-display text-xl font-medium text-ink-900">
                      {p.title}
                    </h3>
                    <p className="mt-2 text-ink-600 text-sm leading-relaxed">
                      {p.desc}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------------- Story (editorial) ---------------- */
const Story = () => {
  return (
    <section
      data-testid="story-section"
      className="relative py-28 md:py-40 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-12 gap-10 lg:gap-20 items-center">
          <Reveal className="col-span-12 lg:col-span-6">
            <div className="relative">
              <div className="rounded-[28px] overflow-hidden border border-ink-100 shadow-[0_20px_50px_-12px_rgba(15,23,42,0.12)] bg-ink-50">
                <img
                  src={PORTRAIT}
                  alt="Justin Chen, founder of Restore.JC"
                  data-testid="story-portrait"
                  loading="lazy"
                  className="w-full h-auto block"
                />
              </div>
              {/* Editorial caption block */}
              <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                <div className="hidden sm:block h-px flex-1 bg-ink-200" />
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-ink-500 font-display">
                    Founder
                  </span>
                  <span className="text-ink-300">·</span>
                  <span className="font-display text-ink-900 text-base">
                    Justin Chen
                  </span>
                  <span className="text-ink-300">·</span>
                  <span className="text-[10px] uppercase tracking-[0.3em] text-ink-500 font-display">
                    Vancouver, BC
                  </span>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="col-span-12 lg:col-span-6">
            <SectionEyebrow testId="story-eyebrow">The Story</SectionEyebrow>
            <h2 className="mt-7 font-display font-light text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-[-0.02em] text-ink-900 leading-[1.05]">
              Built from the
              <br />
              <span className="text-brand-600">inside out.</span>
            </h2>
            <div className="mt-9 space-y-6 text-ink-600 text-lg leading-[1.7] max-w-xl">
              <p>
                In June 2022, Justin was diagnosed with sleep apnea. Fatigue,
                brain fog, and depleted energy had become his baseline.
              </p>
              <p>
                He tested everything — meditation, lifestyle audits, routines,
                supplements, devices, protocols — separating signal from noise.
              </p>
              <p className="text-ink-900">
                Today, he sleeps deeply, performs at his peak, and brings the
                same precision to clients in tech, finance, and high-pressure
                industries.
              </p>
            </div>
            <div className="mt-12 grid grid-cols-3 gap-6">
              {[
                { k: "2022", v: "Diagnosed" },
                { k: "100%", v: "Self-tested" },
                { k: "1:1", v: "Coaching" },
              ].map((s) => (
                <div key={s.k} className="border-l-2 border-brand-600 pl-5">
                  <div className="font-display text-3xl font-light text-ink-900">{s.k}</div>
                  <div className="text-ink-500 text-[11px] uppercase tracking-[0.2em] mt-1.5 font-display">
                    {s.v}
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

/* ---------------- Statement (visually dominant) ---------------- */
const Statement = () => {
  return (
    <section
      data-testid="immersive-section"
      className="relative py-32 md:py-48 lg:py-56 bg-ink-50 overflow-hidden"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 60% 60% at 50% 50%, rgba(37,99,235,0.06), transparent 70%)",
        }}
      />
      <div className="relative max-w-6xl mx-auto px-6 text-center">
        <Reveal>
          <div className="flex items-center justify-center gap-3">
            <span className="h-px w-10 bg-brand-600/60" />
            <span className="font-display text-[10px] uppercase tracking-[0.4em] text-brand-600">
              The Premise
            </span>
            <span className="h-px w-10 bg-brand-600/60" />
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-10 font-display font-light text-[2.5rem] sm:text-5xl md:text-7xl lg:text-[6.5rem] xl:text-[8rem] tracking-[-0.035em] leading-[0.98] text-ink-900 break-words">
            Sleep is the
            <br />
            foundation —
            <br />
            <span className="text-brand-600 italic">everything compounds.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-12 text-ink-600 max-w-xl mx-auto text-lg leading-[1.7]">
            Energy. Cognition. Mood. Recovery. Performance. Fix the foundation,
            and the rest of the stack works.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

/* ---------------- The Restore Method ---------------- */
const Method = () => {
  const pillars = [
    {
      icon: Moon,
      tag: "Pillar 01",
      title: "Sleep Reset",
      desc: "Re-architect your sleep using clinical-grade protocols including sleep restriction therapy, light timing, and circadian alignment.",
    },
    {
      icon: Brain,
      tag: "Pillar 02",
      title: "Nervous System Regulation",
      desc: "Down-regulate stress, calm the wired-but-tired state, and rebuild parasympathetic capacity for true recovery.",
    },
    {
      icon: Sunrise,
      tag: "Pillar 03",
      title: "Lifestyle Optimization",
      desc: "Engineer the daily inputs — nutrition, movement, environment, and tools — that compound into elite performance.",
    },
  ];

  return (
    <section
      data-testid="method-section"
      className="relative py-28 md:py-36 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <Reveal>
          <div className="grid grid-cols-12 gap-10 items-end">
            <div className="col-span-12 lg:col-span-7">
              <SectionEyebrow testId="method-eyebrow">The Restore Method</SectionEyebrow>
              <h2 className="mt-7 font-display font-light text-4xl md:text-5xl lg:text-6xl tracking-[-0.02em] text-ink-900 leading-[1.05]">
                A three-pillar protocol
                <br />
                <span className="text-ink-500">engineered for high performers.</span>
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-5">
              <p className="text-ink-600 leading-[1.7] max-w-md">
                No single intervention fixes sleep. The Restore Method works
                across three interlocking layers — applied in sequence,
                calibrated to you.
              </p>
            </div>
          </div>
        </Reveal>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-6">
          {pillars.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08}>
              <div
                data-testid={`method-card-${i}`}
                className="surface-soft p-9 h-full"
              >
                <div className="flex items-center justify-between">
                  <div className="h-12 w-12 rounded-xl bg-brand-50 flex items-center justify-center">
                    <p.icon className="text-brand-600" size={20} />
                  </div>
                  <span className="font-display text-[10px] uppercase tracking-[0.25em] text-ink-500 num-tag">
                    {p.tag}
                  </span>
                </div>
                <h3 className="mt-8 font-display text-xl font-medium text-ink-900">
                  {p.title}
                </h3>
                <p className="mt-3.5 text-ink-600 leading-[1.7]">{p.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- Services ---------------- */
const Services = () => {
  const services = [
    {
      eyebrow: "1:1 Coaching",
      title: "Sleep Restriction Therapy",
      tag: "Starting at $200/mo",
      desc: "Monthly 1-on-1 program built around evidence-based CBT-I to permanently rewire how you sleep.",
    },
    {
      eyebrow: "Reset Program",
      title: "30-Day Sleep & Gut Reset",
      tag: "Starting at $300",
      desc: "A structured 4-week protocol pairing sleep architecture with gut health for full-system reset.",
    },
    {
      eyebrow: "Products",
      title: "Recommended Sleep Products",
      tag: "Starting at $30",
      desc: "Carefully selected tools and supplements to support better sleep, recovery, and daily energy.",
    },
  ];

  return (
    <section
      data-testid="home-services-section"
      className="relative py-28 md:py-36 bg-ink-50"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <SectionEyebrow testId="services-eyebrow">Programs</SectionEyebrow>
              <h2 className="mt-7 font-display font-light text-3xl sm:text-4xl md:text-5xl tracking-[-0.02em] text-ink-900 leading-[1.05]">
                Coaching, calibrated
                <br />
                <span className="text-ink-500">to your life.</span>
              </h2>
            </div>
            <Link
              to="/services"
              data-testid="services-section-link"
              className="inline-flex items-center gap-2 text-brand-600 hover:text-brand-700 text-sm font-medium"
            >
              View all programs <ArrowUpRight size={16} />
            </Link>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {services.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.08}>
              <Link
                to="/services"
                data-testid={`home-service-card-${i}`}
                className="group surface-soft p-7 h-full flex flex-col hover:border-brand-200 transition-colors"
              >
                <span className="font-display text-[10px] uppercase tracking-[0.25em] text-brand-600">
                  {s.eyebrow}
                </span>
                <h3 className="mt-5 font-display text-2xl font-medium text-ink-900 leading-snug">
                  {s.title}
                </h3>
                <p className="mt-3 text-ink-600 leading-relaxed flex-1">{s.desc}</p>
                <div className="mt-7 pt-5 border-t border-ink-200 flex items-center justify-between">
                  <span className="text-xs text-ink-500 uppercase tracking-[0.2em]">
                    {s.tag}
                  </span>
                  <ArrowRight
                    size={16}
                    className="text-ink-400 transition-all group-hover:text-brand-600 group-hover:translate-x-1"
                  />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- Testimonial ---------------- */
const Testimonial = () => {
  return (
    <section
      data-testid="testimonial-section"
      className="relative py-28 md:py-40 bg-white"
    >
      <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-16">
        <Reveal>
          <SectionEyebrow testId="testimonial-eyebrow">Client Result</SectionEyebrow>
        </Reveal>
        <Reveal delay={0.1}>
          <Quote
            size={40}
            strokeWidth={1.4}
            className="mt-8 text-brand-600/60"
          />
          <blockquote className="mt-5 font-display font-light text-2xl md:text-3xl lg:text-4xl leading-[1.25] tracking-tight text-ink-900">
            &ldquo;Justin&apos;s system rebuilt my sleep from the ground up.
            Within weeks I was falling asleep faster, waking up sharp, and
            finally performing at the level my role demands. This wasn&apos;t
            another sleep app — it was an operating-system upgrade.&rdquo;
          </blockquote>
          <div className="mt-10 flex items-center gap-4">
            <div className="h-12 w-12 rounded-full bg-brand-600 text-white flex items-center justify-center font-display font-medium">
              RK
            </div>
            <div>
              <div className="font-display font-medium text-ink-900">Raj K.</div>
              <div className="text-sm text-ink-500">
                Software Development Manager · Amazon
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

/* ---------------- Final CTA ---------------- */
const FinalCTA = () => {
  return (
    <section
      data-testid="final-cta-section"
      className="relative py-28 md:py-40 bg-ink-50"
    >
      <div className="max-w-4xl mx-auto px-6 text-center">
        <Reveal>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-50 text-brand-700 text-xs font-medium tracking-wide">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-600" />
            Limited 1:1 Availability
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-7 font-display font-light text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-[-0.02em] leading-[1.1] text-ink-900 break-words">
            Start your <span className="text-brand-600">sleep transformation.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-6 text-ink-600 text-lg max-w-xl mx-auto leading-relaxed">
            Book a complimentary sleep assessment. Walk away with clarity, a
            diagnosis, and the next right move — whether you work with me or not.
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <CalendlyButton testId="final-cta-button">Book a Call</CalendlyButton>
            <Link
              to="/contact"
              data-testid="final-cta-message"
              className="inline-flex items-center gap-2 px-5 py-3 text-ink-700 hover:text-brand-600 text-sm transition-colors"
            >
              Or send a message <ArrowRight size={14} />
            </Link>
          </div>
        </Reveal>

        <div className="mt-12 flex flex-wrap justify-center gap-x-8 gap-y-2 text-xs text-ink-500">
          {["Free 20-min call", "No commitment", "Personal reply"].map((t) => (
            <span key={t} className="inline-flex items-center gap-2">
              <CheckCircle2 size={12} className="text-brand-600" /> {t}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- Page ---------------- */
const Home = () => {
  return (
    <main data-testid="home-page" className="bg-white text-ink-900">
      <Hero />
      <Problem />
      <Story />
      <Statement />
      <Method />
      <Services />
      <Testimonial />
      <FinalCTA />
    </main>
  );
};

export default Home;
